import { requireNodeEnvVar } from "../server/utils";

export enum SubscriptionStatus {
  PastDue = "past_due",
  CancelAtPeriodEnd = "cancel_at_period_end",
  Active = "active",
  Deleted = "deleted",
}

export enum PaymentPlanId {
  Free = "free",
  Pro = "pro",
  ProAnnual = "pro_annual",
  Max = "max",
  MaxAnnual = "max_annual",
}

export interface PaymentPlan {
  getPaymentProcessorPlanId: () => string;
  effect: PaymentPlanEffect;
}

export type PaymentPlanEffect =
  | { kind: "subscription" }
  | { kind: "payment" };

export const paymentPlans = {
  [PaymentPlanId.Free]: {
    getPaymentProcessorPlanId: () => "free",
    effect: { kind: "subscription" },
  },
  [PaymentPlanId.Pro]: {
    getPaymentProcessorPlanId: () =>
      requireNodeEnvVar("PAYMENTS_PRO_SUBSCRIPTION_PLAN_ID"),
    effect: { kind: "subscription" },
  },
  [PaymentPlanId.ProAnnual]: {
    getPaymentProcessorPlanId: () =>
      requireNodeEnvVar("PAYMENTS_PRO_ANNUAL_PLAN_ID"),
    effect: { kind: "subscription" },
  },
  [PaymentPlanId.Max]: {
    getPaymentProcessorPlanId: () =>
      requireNodeEnvVar("PAYMENTS_MAX_PLAN_ID"),
    effect: { kind: "subscription" },
  },
  [PaymentPlanId.MaxAnnual]: {
    getPaymentProcessorPlanId: () =>
      requireNodeEnvVar("PAYMENTS_MAX_ANNUAL_PLAN_ID"),
    effect: { kind: "subscription" },
  },
} as const satisfies Record<PaymentPlanId, PaymentPlan>;

export function prettyPaymentPlanName(planId: PaymentPlanId): string {
  const planToName: Record<PaymentPlanId, string> = {
    [PaymentPlanId.Free]: "Free",
    [PaymentPlanId.Pro]: "Pro",
    [PaymentPlanId.ProAnnual]: "Pro Annuel",
    [PaymentPlanId.Max]: "Max",
    [PaymentPlanId.MaxAnnual]: "Max Annuel",
  };
  return planToName[planId];
}

/**
 * Whether a user on this plan still has something to upgrade to.
 * True for Free / Pro / Pro Annuel, false for Max / Max Annuel (already on the
 * top tier) — used to decide who sees the "Upgrade my plan" CTA.
 */
export function canUpgrade(
  subscriptionPlan: string | null | undefined,
): boolean {
  return (
    subscriptionPlan !== PaymentPlanId.Max &&
    subscriptionPlan !== PaymentPlanId.MaxAnnual
  );
}

/**
 * Broker sync (MetaAPI + Tradovate) is the Max-tier advantage. Single source of
 * truth for "does this plan include broker sync" — mirrors PLAN_LIMITS in
 * planLimits.ts. Used by the webhook teardown and the cron plan guards so a
 * downgraded user stops syncing exactly when their plan is no longer Max.
 */
export function planHasBrokerSync(
  subscriptionPlan: string | null | undefined,
): boolean {
  return (
    subscriptionPlan === PaymentPlanId.Max ||
    subscriptionPlan === PaymentPlanId.MaxAnnual
  );
}


/**
 * Whether a plan is a paid tier (Pro / Pro Annuel / Max / Max Annuel) — i.e.
 * anything above Free. Single source of truth for the "Pro or better" feature
 * gates (setup rating, mentor program, …). Always prefer this over comparing
 * against PaymentPlanId.Pro / PaymentPlanId.Max by hand: the annual variants
 * are distinct plan ids and were silently excluded by those hand-rolled checks.
 */
export function planHasPaidTier(
  subscriptionPlan: string | null | undefined,
): boolean {
  return planTierRank(subscriptionPlan) >= 1;
}

/**
 * Edgy (AI analysis) is restricted to the Max tier for the duration of the beta.
 * Same plan set as broker sync today, but deliberately a separate function: the
 * beta lock is a temporary product decision and will be lifted on its own, while
 * broker sync stays Max forever. Sharing one helper would make lifting the beta
 * silently hand Tradovate/MetaAPI to Pro users.
 *
 * The server-side kill for the beta lives behind EDGY_BETA_MAX_ONLY in
 * server/edgy/config.ts — this function only answers "is this plan Max?".
 */
export function planHasEdgyAccess(
  subscriptionPlan: string | null | undefined,
): boolean {
  return (
    subscriptionPlan === PaymentPlanId.Max ||
    subscriptionPlan === PaymentPlanId.MaxAnnual
  );
}

/**
 * Plans that include broker sync, as raw strings — for Prisma `where` filters in
 * the sync crons (`user: { subscriptionPlan: { in: BROKER_SYNC_PLAN_IDS } }`).
 */
export const BROKER_SYNC_PLAN_IDS: string[] = [
  PaymentPlanId.Max,
  PaymentPlanId.MaxAnnual,
];

/**
 * Duree de la periode de grace apres un echec de prelevement, en jours.
 *
 * C'est Edgely qui fait autorite sur la coupure, PAS Stripe : le dashboard
 * Stripe est configure pour laisser l'abonnement en `past_due` sans jamais
 * l'annuler, et `paymentFailedAt` + cette constante decident seuls. Deux
 * horloges concurrentes (relances Stripe d'un cote, grace de l'autre)
 * finiraient par se contredire, et on annoncerait 7 jours en coupant au 3e.
 */
export const PAYMENT_GRACE_PERIOD_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Fin de la periode de grace, ou null si aucun echec en cours. */
export function gracePeriodEndsAt(
  paymentFailedAt: Date | string | null | undefined,
): Date | null {
  if (!paymentFailedAt) return null;
  const startedAt = new Date(paymentFailedAt);
  // Une date invalide (colonne corrompue, chaine vide) ne doit pas produire un
  // NaN qui rendrait toutes les comparaisons fausses et couperait l'acces.
  if (Number.isNaN(startedAt.getTime())) return null;
  return new Date(startedAt.getTime() + PAYMENT_GRACE_PERIOD_DAYS * MS_PER_DAY);
}

/**
 * Grace ecoulee : l'utilisateur doit etre traite comme Free.
 *
 * `now` est injectable pour les tests et pour que le job et le gate partagent
 * exactement le meme instant sur une fournee.
 */
export function isGracePeriodExpired(
  paymentFailedAt: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  const endsAt = gracePeriodEndsAt(paymentFailedAt);
  if (!endsAt) return false;
  return now.getTime() >= endsAt.getTime();
}

/** Jours entiers restants avant retrogradation (0 si expiree ou absente).
 *  Arrondi au superieur : a J+6h il reste « 7 jours », pas 6. */
export function graceDaysRemaining(
  paymentFailedAt: Date | string | null | undefined,
  now: Date = new Date(),
): number {
  const endsAt = gracePeriodEndsAt(paymentFailedAt);
  if (!endsAt) return 0;
  const remainingMs = endsAt.getTime() - now.getTime();
  if (remainingMs <= 0) return 0;
  return Math.ceil(remainingMs / MS_PER_DAY);
}

/** The two billing tiers a user can actively choose between. */
export type PlanTier = "pro" | "max";

/** Tier rank for upgrade/downgrade detection. Free < Pro(Annual) < Max(Annual). */
export function planTierRank(
  subscriptionPlan: string | null | undefined,
): 0 | 1 | 2 {
  if (
    subscriptionPlan === PaymentPlanId.Max ||
    subscriptionPlan === PaymentPlanId.MaxAnnual
  ) {
    return 2;
  }
  if (
    subscriptionPlan === PaymentPlanId.Pro ||
    subscriptionPlan === PaymentPlanId.ProAnnual
  ) {
    return 1;
  }
  return 0;
}

/** Whether a plan is billed annually (vs monthly). */
export function isAnnualPlan(
  subscriptionPlan: string | null | undefined,
): boolean {
  return (
    subscriptionPlan === PaymentPlanId.ProAnnual ||
    subscriptionPlan === PaymentPlanId.MaxAnnual
  );
}

/**
 * Resolve the concrete PaymentPlanId for a target tier while preserving the
 * user's current billing periodicity. A Pro Annuel user upgrading to Max lands
 * on Max Annuel, not Max monthly.
 */
export function resolvePlanIdForTier(
  tier: PlanTier,
  keepAnnual: boolean,
): PaymentPlanId {
  if (tier === "max") {
    return keepAnnual ? PaymentPlanId.MaxAnnual : PaymentPlanId.Max;
  }
  return keepAnnual ? PaymentPlanId.ProAnnual : PaymentPlanId.Pro;
}

export function parsePaymentPlanId(planId: string): PaymentPlanId {
  if ((Object.values(PaymentPlanId) as string[]).includes(planId)) {
    return planId as PaymentPlanId;
  } else {
    throw new Error(`Invalid PaymentPlanId: ${planId}`);
  }
}

export function getSubscriptionPaymentPlanIds(): PaymentPlanId[] {
  return Object.values(PaymentPlanId).filter(
    (planId) => paymentPlans[planId].effect.kind === "subscription",
  );
}

export function getPaymentPlanIdByPaymentProcessorPlanId(
  paymentProcessorPlanId: string,
): PaymentPlanId {
  for (const [planId, plan] of Object.entries(paymentPlans)) {
    if (plan.getPaymentProcessorPlanId() === paymentProcessorPlanId) {
      return planId as PaymentPlanId;
    }
  }

  throw new Error(
    `Unknown payment processor plan ID: ${paymentProcessorPlanId}`,
  );
}

/** Utilisateur minimal pour résoudre le plan effectif (client et serveur). */
export interface EffectivePlanUser {
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
  paymentFailedAt?: Date | string | null;
}

/**
 * Plan effectivement accordé : Free si pas de plan, abonnement supprimé ou
 * grâce expirée. Source unique, partagée par `getUserPlan` (serveur) et par
 * le client (choix du mode Lite / Advanced), pour que les deux côtés ne
 * divergent jamais.
 */
export function resolveEffectivePlan(user: EffectivePlanUser): PaymentPlanId {
  if (!user.subscriptionPlan) return PaymentPlanId.Free;
  if (user.subscriptionStatus === SubscriptionStatus.Deleted) return PaymentPlanId.Free;
  if (isGracePeriodExpired(user.paymentFailedAt)) return PaymentPlanId.Free;
  const planId = user.subscriptionPlan as PaymentPlanId;
  return Object.values(PaymentPlanId).includes(planId) ? planId : PaymentPlanId.Free;
}

/**
 * Cet utilisateur a-t-il DROIT à la sync broker, maintenant ?
 *
 * ⚠️ À préférer à `planHasBrokerSync(user.subscriptionPlan)` partout où l'on
 * décide d'un droit d'accès. Une résiliation effective laisse `subscriptionPlan`
 * à `max` et ne passe que `subscriptionStatus` à `deleted` : juger sur le plan
 * brut laissait un ancien abonné reconnecter Tradovate et resynchroniser sans
 * payer. Le plan EFFECTIF couvre aussi la grâce d'impayé expirée.
 */
export function userHasBrokerSync(user: EffectivePlanUser): boolean {
  return planHasBrokerSync(resolveEffectivePlan(user));
}
